// src/lib/applicationInsights.ts
import {
  ApplicationInsights,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

export const reactPlugin = new ReactPlugin();

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as
  | string
  | undefined;
// VITE_APP_ENV is set per deploy workflow (staging/production). Falls back to Vite's MODE
// for local dev — needed because `vite build` reports MODE='production' for BOTH staging and prod.
const environment =
  (import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE;
const appVersion =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown';

// Property keys that must never reach Azure (auth tokens, cookies, secrets).
const SENSITIVE_KEY = /authorization|cookie|token|secret|password|bearer/i;

// Query-string parameter names whose VALUES must never reach Azure. Deliberately
// wider than SENSITIVE_KEY: these names are defined by third-party APIs (Meta
// Graph's `access_token`, Google's `key`), not by our own code, so casting a
// broad net is free here — whereas widening SENSITIVE_KEY would start redacting
// legitimate custom properties like `metricKey` or `periodKey`.
const SENSITIVE_PARAM =
  /token|secret|password|passwd|pwd|auth|credential|signature|key/i;

/**
 * Redact credentials carried in a URL's query string.
 *
 * Ajax/fetch auto-collection is ON, so every request is recorded as a
 * dependency with its full URL on `baseData.data` — and at least one dependency
 * (the Meta Graph API, see `meta-dashboard-v2/apis/metaGraphApi.ts`) takes its
 * access token as a query param rather than a header. The `properties` scrub
 * below does not reach these fields, so without this the token would be written
 * to the `dependencies` table in plaintext. Meta's `paging.next` cursor URLs
 * carry the token too and go through the same path.
 *
 * Non-sensitive params are kept — knowing which `fields` or `time_range` a slow
 * Graph call asked for is the point of dependency telemetry.
 */
const redactUrl = (value: string): string => {
  const queryStart = value.indexOf('?');
  if (queryStart === -1) return value;

  // Hand-split rather than `new URL()`: dependency `name` is a method + path
  // fragment ("GET /v21.0/act_1/campaigns"), not an absolute URL, and a throw
  // inside a telemetry initializer would drop the item entirely.
  const redacted = value
    .slice(queryStart + 1)
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const name = pair.slice(0, eq);
      return SENSITIVE_PARAM.test(name) ? `${name}=[REDACTED]` : pair;
    })
    .join('&');

  return `${value.slice(0, queryStart)}?${redacted}`;
};

export const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],

    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,

    // Header tracking is OFF: the API client attaches `Authorization: Bearer <token>`,
    // and capturing request/response headers would store those tokens in Azure.
    enableRequestHeaderTracking: false,
    enableResponseHeaderTracking: false,

    autoTrackPageVisitTime: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,

    // Adjust this if you get too much telemetry in production.
    samplingPercentage: 100,
  },
});

// Defense-in-depth scrub + environment/release tagging on every telemetry item.
const telemetryInitializer = (item: ITelemetryItem): boolean | void => {
  // Identify this app in the portal.
  item.tags = item.tags ?? {};
  item.tags['ai.cloud.role'] = 'brokercopilot-frontend';

  // Stamp environment + release so dev noise can be filtered and regressions tied to deploys.
  item.data = item.data ?? {};
  item.data.environment = environment;
  item.data.appVersion = appVersion;

  // Redact anything sensitive that slipped into custom properties.
  const props = item.baseData?.properties as
    | Record<string, unknown>
    | undefined;
  if (props) {
    for (const key of Object.keys(props)) {
      if (SENSITIVE_KEY.test(key)) props[key] = '[REDACTED]';
    }
  }

  // Redact credentials in URLs. Dependencies record the request URL on `data`
  // (and sometimes `name`/`target`); page views record the route on `uri`.
  const baseData = item.baseData;
  if (baseData) {
    for (const field of ['data', 'name', 'target', 'uri'] as const) {
      const value: unknown = baseData[field];
      if (typeof value === 'string') baseData[field] = redactUrl(value);
    }
  }
};

export const initializeApplicationInsights = () => {
  if (!connectionString) {
    console.warn('Application Insights connection string is missing.');
    return;
  }

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(telemetryInitializer);
  appInsights.trackPageView();
};

/** Correlate telemetry to a user by id (no PII). Call on login / session restore. */
export const setTelemetryUser = (userId: string | number) => {
  console.log('Setting telemetry user:', userId);
  if (!connectionString || userId == null) return;
  appInsights.setAuthenticatedUserContext(String(userId), undefined, true);
};

/** Drop user correlation. Call on logout / session expiry. */
export const clearTelemetryUser = () => {
  if (!connectionString) return;
  appInsights.clearAuthenticatedUserContext();
};
